"use client";

import React from "react";

import { useCountdown } from "@/lib/utilHooks";

// Hero countdown card (Figma 5143:1620 desktop, 5242:16554 mobile) — white panel
// pinned to the bottom of the hero figure: event caption, hairline, then four
// serif digit groups split by faded colons. Digits come from the shared
// `useCountdown` hook (SSR-safe: "00" until mount). The mobile card is the
// desktop one scaled by ~0.72, which the two rem sets below reproduce.

const LABELS = ["days", "hours", "minutes", "seconds"] as const;

function Unit({ value, label }: { value: string; label: string }) {
  return (
    <div className="flex flex-col justify-center items-center whitespace-nowrap gap-[0.9rem] lg:gap-[1.25rem]">
      <p className="mb-0 leading-none text-black font-news text-[5.75rem] tracking-[-0.29rem] lg:text-[8rem] lg:tracking-[-0.4rem]">
        {value}
      </p>
      <p className="mb-0 uppercase font-geist text-black/50 leading-[1.5] text-[1.5rem] lg:text-[1.75rem]">{label}</p>
    </div>
  );
}

function Colon() {
  return (
    <div className="flex flex-col justify-center items-center shrink-0 w-[2.33rem] pt-[0.9rem] lg:w-[3.25rem] lg:pt-[1.25rem]">
      <p className="mb-0 w-full leading-none text-center text-black opacity-[0.34] font-news text-[5rem] tracking-[-0.25rem] lg:text-[7rem] lg:tracking-[-0.35rem]">
        :
      </p>
    </div>
  );
}

export default function HeroCountdown({
  date,
  caption,
  labels = LABELS,
}: {
  date?: string;
  caption?: string;
  /** Unit captions in day → second order. */
  labels?: readonly [string, string, string, string];
}) {
  const { days, hours, minutes, seconds } = useCountdown(date ?? null);
  const values = [days, hours, minutes, seconds];

  return (
    <div className="flex flex-col justify-center items-center w-full bg-white backdrop-blur-[1.8rem] rounded-[0.8rem] gap-[1.6rem] py-[2.15rem] lg:rounded-[1.125rem] lg:gap-[2.25rem] lg:py-[3rem]">
      {caption && (
        <p className="mb-0 text-center text-black uppercase font-geist text-[1.5rem] leading-[1.1] lg:text-[1.75rem] lg:leading-[1.33] lg:tracking-[0.12rem]">
          {caption}
        </p>
      )}

      <hr
        aria-hidden
        className="m-0 w-full border-0 border-t border-black/10"
      />

      <div className="flex justify-center items-start gap-[1.25rem] lg:gap-[1.75rem]">
        {values.map((value, i) => (
          <React.Fragment key={labels[i]}>
            {i > 0 && <Colon />}
            <Unit
              value={value}
              label={labels[i]}
            />
          </React.Fragment>
        ))}
      </div>
    </div>
  );
}
